import {
  ApiDataResponse,
  AdminCategoryListWithPaginationDTO,
  AdminCategoryListWithPaginationResponse,
  CategoryListDTO,
  CategoryListResponse,
  CategoryResponse,
  CategoryDataResponse,
  EventDTO,
  EventListDTO,
  EventResponse,
  FilteredEventListResponse,
  PublishToPlatformsResponseData,
  UpdateEventPricingRequest,
  UpdateEventRequest,
} from "@root/utils/dtos";
import { Category } from "@root/utils/entities";
import { ApiTransport } from "./config/apiTransport";
import {
  DateRange,
  MapBounds,
  NumberRange,
  Pagination,
  DeleteScope,
} from "@root/utils/types";
import { EventSearchFilters } from "@root/stores/eventPageStore";
import { Coordinate } from "@root/utils/entities";

export class EventService {
  private readonly _apiTransport: ApiTransport;

  constructor(sessionCookie?: string) {
    this._apiTransport = new ApiTransport(sessionCookie);
  }

  searchEvents = (
    page: number,
    address?: string,
    mileRadius?: number,
    ageRange?: NumberRange,
    dateRange?: DateRange,
    categories?: string[],
    mapBounds?: MapBounds,
    coordinate?: Coordinate
  ): FilteredEventListResponse => {
    const data = [`page=${page}`];

    if (mapBounds) {
      data.push(`neLat=${mapBounds.northEast.latitude}`);
      data.push(`neLng=${mapBounds.northEast.longitude}`);
      data.push(`swLat=${mapBounds.southWest.latitude}`);
      data.push(`swLng=${mapBounds.southWest.longitude}`);
    } else if (address) {
      data.push(`address=${encodeURIComponent(address)}`);
      if (mileRadius) data.push(`mileRadius=${mileRadius}`);
    } else if (coordinate) {
      data.push(`lat=${coordinate.latitude}`);
      data.push(`lng=${coordinate.longitude}`);
      if (mileRadius) data.push(`mileRadius=${mileRadius}`);
    }

    if (ageRange?.min) data.push(`minAge=${ageRange?.min}`);
    if (ageRange?.max) data.push(`maxAge=${ageRange?.max}`);
    if (dateRange?.start) data.push(`start=${dateRange?.start?.toISOString()}`);
    if (dateRange?.end) data.push(`end=${dateRange?.end?.toISOString()}`);
    if (categories && categories.length > 0)
      data.push(`categories=${categories.join("&categories=")}`);
    let qs = ``;
    if (data.length > 0) qs = `?${data.join("&")}`;

    return this._apiTransport.get<
      EventListDTO & { searchFilters: EventSearchFilters } & {
        pagination: Pagination;
      }
    >(`/api/v1/events${qs}`);
  };

  getEvent = (id: string): EventResponse =>
    this._apiTransport.get<EventDTO>(`/api/v1/events/${id}`);

  createEventCheckoutSession = (eventId: string, pricingId: string) =>
    this._apiTransport.post<{ sessionId: string; url: string }>(
      `/api/v1/events/${eventId}/checkout-session`,
      { pricingId }
    );

  getCalendarFile = (id: string): Promise<ApiDataResponse<Blob>> =>
    this._apiTransport.get<Blob>(`/api/v1/events/${id}/ics`);

  getCategories = (): CategoryListResponse =>
    this._apiTransport.get<CategoryListDTO>(`/api/v1/events/categories`);

  searchCategories = (searchTerm: string): CategoryListResponse => {
    const queryString = searchTerm
      ? `?search=${encodeURIComponent(searchTerm)}`
      : "";
    return this._apiTransport.get<CategoryListDTO>(
      `/api/v1/events/categories${queryString}`
    );
  };

  validateEventCreationLimit = (
    userId?: string,
    organizationId?: string
  ): Promise<ApiDataResponse<any>> => {
    const params: string[] = [];
    if (userId) {
      params.push(`userId=${encodeURIComponent(userId)}`);
    }
    if (organizationId) {
      params.push(`organizationId=${encodeURIComponent(organizationId)}`);
    }
    const queryString = params.length > 0 ? `?${params.join("&")}` : "";
    return this._apiTransport.get<any>(
      `/api/v1/events/validate-creation-limit${queryString}`
    );
  };

  createEvent = (request: UpdateEventRequest): EventResponse =>
    this._apiTransport.post<EventDTO>(`/api/v1/events`, request);

  updateEvent = (
    id: string,
    request: UpdateEventRequest,
    scope?: DeleteScope
  ): EventResponse => {
    if (scope) {
      request.scope = scope;
    }
    return this._apiTransport.put<EventDTO>(`/api/v1/events/${id}`, request);
  };

  publishToPlatforms = (eventId: string, selectedPlatforms: string[]): Promise<ApiDataResponse<PublishToPlatformsResponseData>> => {
    const formData = new FormData();
    formData.append("selectedPlatforms", selectedPlatforms.join(","));
    return this._apiTransport.postMultipart<PublishToPlatformsResponseData>(
      `/api/v1/events/${eventId}/platforms/publish`,
      formData
    );
  };

  updateEventBannerImage = (eventId: string, formData: FormData, scope?: DeleteScope, childEventIds?: string[]) => {
    const qs = scope ? `?scope=${scope}` : "";
    if (childEventIds?.length) {
      formData.append("childEventIds", childEventIds.join(","));
    }
    return this._apiTransport.postMultipart(
      `/api/v1/events/${eventId}/images/banner${qs}`,
      formData
    );
  };

  updateEventIcon = (eventId: string, formData: FormData, scope?: DeleteScope) => {
    const qs = scope ? `?scope=${scope}` : "";
    return this._apiTransport.postMultipart(
      `/api/v1/events/${eventId}/images/icon${qs}`,
      formData
    );
  };

  cancelEvent = (id: string, scope?: DeleteScope, isHardDelete?: boolean) => {
    const params: string[] = [];
    if (scope) params.push(`scope=${scope}`);
    if (isHardDelete) params.push(`isHardDelete=true`);
    const qs = params.length > 0 ? `?${params.join("&")}` : "";
    return this._apiTransport.delete(`/api/v1/events/${id}${qs}`);
  };

  reassignEvent = (id: string, organizationId?: string, userId?: string) =>
    this._apiTransport.patch(`/api/v1/events/${id}/reassign`, {
      eventId: id,
      newOrganizationId: organizationId,
      newUserId: userId,
    });

  bulkCancelEvents = (ids: string[], isHardDelete: boolean = false, scope: DeleteScope = "Single") => {
    return this._apiTransport.delete(`/api/v1/events/bulk-delete`, {
      body: JSON.stringify({ ids, isHardDelete, scope }),
    });
  };

  addPricing = (eventId: string, request: UpdateEventPricingRequest, scope?: DeleteScope, childEventIds?: string[]) => {
    const qs = scope ? `?scope=${scope}` : "";
    return this._apiTransport.post(`/api/v1/events/${eventId}/prices${qs}`, { ...request, childEventIds });
  };

  updatePricing = (
    eventId: string,
    pricingId: string,
    request: UpdateEventPricingRequest,
    scope?: DeleteScope
  ) => {
    const qs = scope ? `?scope=${scope}` : "";
    return this._apiTransport.put(
      `/api/v1/events/${eventId}/prices/${pricingId}${qs}`,
      request
    );
  };

  deletePricing = (eventId: string, pricingId: string, scope?: DeleteScope) => {
    const qs = scope ? `?scope=${scope}` : "";
    return this._apiTransport.delete(`/api/v1/events/${eventId}/prices/${pricingId}${qs}`);
  };

  getAllEvents = (
    page: number = 0,
    pageSize: number = 25
  ): Promise<ApiDataResponse<EventListDTO & { pagination: Pagination }>> =>
    this._apiTransport.get<EventListDTO & { pagination: Pagination }>(
      `/api/v1/events/all-events?page=${page}${pageSize !== 25 ? `&pageSize=${pageSize}` : ""}`
    );

  // Category Management API methods
  getAllCategories = (
    includeDeleted: boolean = false,
    parentCategoryId?: string,
    page: number = 0,
    pageSize: number = 25
  ): AdminCategoryListWithPaginationResponse => {
    const params: string[] = [];
    params.push(`page=${page}`);
    params.push(`pageSize=${pageSize}`);
    if (includeDeleted) params.push("includeDeleted=true");
    if (parentCategoryId)
      params.push(`parentCategoryId=${encodeURIComponent(parentCategoryId)}`);
    const queryString = `?${params.join("&")}`;
    return this._apiTransport.get<AdminCategoryListWithPaginationDTO>(
      `/api/v1/categories${queryString}`
    );
  };

  getCategory = (id: string): CategoryDataResponse =>
    this._apiTransport.get<Category>(`/api/v1/categories/${id}`);

  createCategory = (
    name: string,
    parentCategoryId: string | null = null
  ): CategoryResponse =>
    this._apiTransport.post<{ category: Category }>(`/api/v1/categories`, {
      name,
      parentCategoryId,
    });

  updateCategory = (
    id: string,
    name: string,
    parentCategoryId: string | null = null
  ): CategoryResponse =>
    this._apiTransport.put<{ category: Category }>(`/api/v1/categories/${id}`, {
      name,
      parentCategoryId,
    });

  deleteCategory = (id: string): Promise<ApiDataResponse<never>> =>
    this._apiTransport.delete<never>(`/api/v1/categories/${id}`);
}

export const eventService = new EventService();
